import { useLocation } from 'wouter';
import {
  Activity,
  ChartNoAxesCombined,
  ChevronLeft,
  ChevronRight,
  FileCheck2,
  LayoutDashboard,
  Scale,
  Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BrandLogo, Logo } from '@/components/logo';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const navigationSections = [
  {
    label: 'Review',
    items: [
      { name: 'Decision', href: '/', icon: LayoutDashboard },
      { name: 'Strategy', href: '/strategy', icon: Target },
      { name: 'Performance', href: '/performance', icon: ChartNoAxesCombined },
    ],
  },
  {
    label: 'Diligence',
    items: [
      { name: 'Operations', href: '/operations', icon: Activity },
      { name: 'Evidence', href: '/evidence', icon: FileCheck2 },
      { name: 'Governance', href: '/governance', icon: Scale },
    ],
  },
] as const;

export default function Sidebar({
  collapsed,
  onToggle,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const [location, setLocation] = useLocation();
  const pathname = location.split('?')[0] || '/';

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  const NavItem = ({ item }: { item: (typeof navigationSections)[number]['items'][number] }) => {
    const active = isActive(item.href);
    const Icon = item.icon;

    const handleNavClick = () => {
      setLocation(item.href);
      if (onMobileClose) onMobileClose();
    };

    const content = (
      <Button
        className={`relative h-auto w-full justify-start rounded-md px-3 py-2.5 transition ${
          active
            ? 'bg-[#7AD8CC]/12 text-[#7AD8CC] shadow-[inset_2px_0_0_rgba(122,216,204,0.95)] icon-glow'
            : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100'
        }`}
        data-testid={`nav-${item.href === '/' ? 'decision' : item.href.slice(1)}`}
        onClick={handleNavClick}
        type="button"
        variant="ghost"
      >
        <Icon className={`${collapsed ? 'mr-0' : 'mr-3'} h-5 w-5 shrink-0`} />
        {!collapsed && <span className="truncate text-sm">{item.name}</span>}
      </Button>
    );

    if (!collapsed) {
      return content;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">{item.name}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={onMobileClose}
        />
      ) : null}

      <aside
        aria-label="Primary navigation"
        className={`qf-sidebar fixed left-0 top-0 z-40 h-screen border-r transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-72'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        <div className="flex h-full flex-col">
          <div
            className={`border-b border-white/10 px-4 py-5 ${
              collapsed ? 'flex items-center justify-center' : 'flex items-center justify-between'
            }`}
          >
            {collapsed ? (
              <Logo className="h-9 w-9" />
            ) : (
              <BrandLogo
                className="min-w-0 gap-2.5"
                markClassName="h-8 w-8"
                tone="auto"
                wordmarkClassName="h-5 w-auto max-w-[144px]"
              />
            )}
            <Button
              aria-label="Close navigation menu"
              className="p-2 text-slate-400 hover:text-slate-100 md:hidden"
              onClick={onMobileClose}
              size="sm"
              variant="ghost"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>

          <Button
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute right-0 top-6 z-10 hidden h-8 w-8 translate-x-1/2 items-center justify-center rounded-full border border-white/10 bg-[#10181D] p-0 text-slate-400 shadow-lg hover:bg-[#17242B] hover:text-slate-100 md:flex"
            data-testid="button-toggle-sidebar"
            onClick={onToggle}
            size="sm"
            type="button"
            variant="ghost"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>

          <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-5">
            {navigationSections.map((section) => (
              <div key={section.label} className="space-y-1">
                {!collapsed ? (
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                    {section.label}
                  </p>
                ) : null}
                {section.items.map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </div>
            ))}
          </nav>
        </div>
      </aside>
    </>
  );
}
